import { Box } from "@chakra-ui/react";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  floatingScrollbarLayout,
  type FloatingScrollbarLayout,
} from "./floatingScrollbarLayout";

/**
 * FloatingScrollbar — a horizontal scrollbar pinned to the bottom of the
 * viewport, over the table's width, while the table's own scrollbar is below
 * the fold (PLO-6217). It is a proxy: an empty track as wide as the table's
 * content, with its scrollLeft mirrored both ways. `position: fixed` rather
 * than sticky, because the table sits inside `overflow: hidden` boxes (they
 * clip the rounded corners), which would trap a sticky bar.
 *
 * The box has no height of its own: it is a 1px spacer plus the platform's
 * scrollbar, so an overlay-scrollbar setup (macOS trackpad) paints nothing
 * until the user scrolls, exactly like the native bar it stands in for.
 */
const FloatingScrollbar = ({
  targetRef,
}: {
  targetRef: React.RefObject<HTMLDivElement>;
}) => {
  const barRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState<FloatingScrollbarLayout | null>(null);

  useEffect(() => {
    const target = targetRef.current;
    if (!target) return;

    let frame = 0;
    const measure = () => {
      frame = 0;
      const rect = target.getBoundingClientRect();
      const next = floatingScrollbarLayout({
        rect,
        clientWidth: target.clientWidth,
        scrollWidth: target.scrollWidth,
        viewportHeight: window.innerHeight,
      });
      setLayout((prev) =>
        prev?.left === next?.left &&
        prev?.width === next?.width &&
        prev?.scrollWidth === next?.scrollWidth
          ? prev
          : next,
      );
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    const syncFromTarget = () => {
      const bar = barRef.current;
      if (bar && bar.scrollLeft !== target.scrollLeft) {
        bar.scrollLeft = target.scrollLeft;
      }
    };

    // Capture phase: the page may scroll on any ancestor, not just window.
    window.addEventListener("scroll", schedule, true);
    window.addEventListener("resize", schedule);
    target.addEventListener("scroll", syncFromTarget);
    // Column resizes and toggles change the content width; rows loading or a
    // banner above the table move its bottom edge without a scroll event.
    const observer = new ResizeObserver(schedule);
    observer.observe(target);
    if (target.firstElementChild) observer.observe(target.firstElementChild);
    observer.observe(document.documentElement);
    measure();

    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule, true);
      window.removeEventListener("resize", schedule);
      target.removeEventListener("scroll", syncFromTarget);
      observer.disconnect();
    };
  }, [targetRef]);

  // A freshly shown bar starts at 0 — line it up with the table.
  const isShown = layout !== null;
  useLayoutEffect(() => {
    const bar = barRef.current;
    const target = targetRef.current;
    if (bar && target) bar.scrollLeft = target.scrollLeft;
  }, [isShown, targetRef]);

  if (!layout) return null;

  return (
    <Box
      ref={barRef}
      data-testid="floating-scrollbar"
      position="fixed"
      bottom={0}
      left={`${layout.left}px`}
      width={`${layout.width}px`}
      overflowX="scroll"
      overflowY="hidden"
      // Above the table's sticky header/pinned column, below the floating
      // action bar (zIndex 10) and every Chakra overlay.
      zIndex={5}
      onScroll={(e) => {
        const target = targetRef.current;
        const left = e.currentTarget.scrollLeft;
        if (target && target.scrollLeft !== left) target.scrollLeft = left;
      }}
    >
      <Box width={`${layout.scrollWidth}px`} height="1px" />
    </Box>
  );
};

export default FloatingScrollbar;
